import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { lazyWithReload } from "@/lib/lazyWithReload";

import { AppNav } from "@/components/NavBar";
import { ActingAsBanner } from "@/components/ActingAsBanner";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { IdleLogout } from "@/components/IdleLogout";
import { Footer } from "@/components/Footer";
import { RequireAuth } from "@/components/RequireAuth";
import { RouteFallback } from "@/components/RouteFallback";
import { AuthProvider } from "@/auth/AuthContext";
import { ThemeProvider } from "@/theme/ThemeProvider";

const Dashboard = lazyWithReload(() => import("@/routes/Dashboard"));
const Login = lazyWithReload(() => import("@/routes/Login"));
const ZitadelCallback = lazyWithReload(() => import("@/routes/ZitadelCallback"));
const ConnectionsList = lazyWithReload(() => import("@/routes/Connections/ConnectionsList"));
const ConnectionDetail = lazyWithReload(() => import("@/routes/Connections/ConnectionDetail"));
const NewConnection = lazyWithReload(() => import("@/routes/Connections/NewConnection"));
const ServiceInventory = lazyWithReload(() => import("@/routes/ServiceInventory"));
const TelemetryMapping = lazyWithReload(() => import("@/routes/TelemetryMapping"));
const SchedulesList = lazyWithReload(
  () => import("@/routes/Connections/Schedules/SchedulesList"),
);
const ScheduleEdit = lazyWithReload(
  () => import("@/routes/Connections/Schedules/ScheduleEdit"),
);
const GroupsList = lazyWithReload(() => import("@/routes/Connections/Groups/GroupsList"));
const GroupEdit = lazyWithReload(() => import("@/routes/Connections/Groups/GroupEdit"));
const Profile = lazyWithReload(() => import("@/routes/Profile/Profile"));
const AdminUsers = lazyWithReload(() => import("@/routes/Admin/AdminUsers"));
const AdminRoles = lazyWithReload(() => import("@/routes/Admin/AdminRoles"));
const AdminSettings = lazyWithReload(() => import("@/routes/Admin/AdminSettings"));
const AdminIdentityProviders = lazyWithReload(() => import("@/routes/Admin/AdminIdentityProviders"));
const AdminLumenAccounts = lazyWithReload(() => import("@/routes/Admin/AdminLumenAccounts"));
const AdminBandwidthLadders = lazyWithReload(() => import("@/routes/Admin/AdminBandwidthLadders"));
const AdminAuditLog = lazyWithReload(() => import("@/routes/Admin/AdminAuditLog"));
const AdminJobs = lazyWithReload(() => import("@/routes/Admin/AdminJobs"));
const AdminJobDetail = lazyWithReload(() => import("@/routes/Admin/AdminJobDetail"));
const AdminEmailPreview = lazyWithReload(() => import("@/routes/Admin/AdminEmailPreview"));
const PlatformTenants = lazyWithReload(() => import("@/routes/Admin/PlatformTenants"));
const PlatformSettings = lazyWithReload(() => import("@/routes/Admin/PlatformSettings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

export function App() {
  // Zitadel is the sole auth path — the tenant comes from the token, so there's
  // no apex/subdomain split anymore; one route tree serves every request.
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            {/* Sticky-footer layout: outer column fills the viewport; the routes
                wrapper grows; the footer hugs the bottom. Inner Suspense (in
                AppRoutes) keeps the navbar/footer during route chunk loads. */}
            <div className="d-flex flex-column" style={{ minHeight: "100vh" }}>
              <main className="flex-grow-1">
                <Suspense fallback={<RouteFallback />}>
                  <AppRoutes />
                </Suspense>
              </main>
              <Footer />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function AppRoutes() {
  return (
    <>
      <AppNav />
      <IdleLogout />
      <ActingAsBanner />
      <MaintenanceBanner />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso/callback" element={<ZitadelCallback />} />
          <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/connections" element={<RequireAuth><ConnectionsList /></RequireAuth>} />
          <Route path="/connections/new" element={<RequireAuth><NewConnection /></RequireAuth>} />
          <Route path="/inventory" element={<RequireAuth><ServiceInventory /></RequireAuth>} />
          <Route path="/telemetry" element={<RequireAuth><TelemetryMapping /></RequireAuth>} />
          {/* Schedules routes — must come BEFORE /connections/:id so the literal
              segment doesn't get shadowed by the UUID-shaped catch-all. */}
          <Route path="/connections/groups" element={<RequireAuth><GroupsList /></RequireAuth>} />
          <Route path="/connections/groups/new" element={<RequireAuth><GroupEdit /></RequireAuth>} />
          <Route path="/connections/groups/:groupId" element={<RequireAuth><GroupEdit /></RequireAuth>} />
          <Route path="/connections/schedules" element={<RequireAuth><SchedulesList /></RequireAuth>} />
          <Route path="/connections/schedules/new" element={<RequireAuth><ScheduleEdit /></RequireAuth>} />
          <Route path="/connections/schedules/:scheduleId" element={<RequireAuth><ScheduleEdit /></RequireAuth>} />
          <Route path="/connections/:id" element={<RequireAuth><ConnectionDetail /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/admin/users" element={<RequireAuth><AdminUsers /></RequireAuth>} />
          <Route path="/admin/roles" element={<RequireAuth><AdminRoles /></RequireAuth>} />
          <Route path="/admin/settings" element={<RequireAuth><AdminSettings /></RequireAuth>} />
          <Route path="/admin/identity-providers" element={<RequireAuth><AdminIdentityProviders /></RequireAuth>} />
          <Route path="/admin/lumen-accounts" element={<RequireAuth><AdminLumenAccounts /></RequireAuth>} />
          <Route path="/admin/bandwidth-ladders" element={<RequireAuth><AdminBandwidthLadders /></RequireAuth>} />
          <Route path="/admin/platform/tenants" element={<RequireAuth><PlatformTenants /></RequireAuth>} />
          <Route path="/admin/platform/settings" element={<RequireAuth><PlatformSettings /></RequireAuth>} />
          <Route path="/admin/audit-log" element={<RequireAuth><AdminAuditLog /></RequireAuth>} />
          <Route path="/admin/jobs" element={<RequireAuth><AdminJobs /></RequireAuth>} />
          <Route path="/admin/jobs/:taskName" element={<RequireAuth><AdminJobDetail /></RequireAuth>} />
          <Route path="/admin/emails/preview" element={<RequireAuth><AdminEmailPreview /></RequireAuth>} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </Suspense>
    </>
  );
}

function RootRedirect() {
  window.location.replace("/dashboard");
  return null;
}
